import { useState, useEffect } from 'react';
import { message } from 'antd';
import { BoardService } from './boardService';

export const useTaskBoard = () => {
    // State management
    const [boards, setBoards] = useState([]);
    const [currentBoard, setCurrentBoard] = useState(null);
    const [properties, setProperties] = useState([]);
    const [manualClients, setManualClients] = useState([]);
    const [groups, setGroups] = useState([]);
    const [modalState, setModalState] = useState({
        visible: false,
        type: null, // 'create' or 'edit'
        loading: false
    });
    const [pageLoading, setPageLoading] = useState(true);
    const [deleteLoading, setDeleteLoading] = useState(null);

    // Fetch initial data
    useEffect(() => {
        fetchBoards();
        fetchProperties();
        fetchManualClients();
        fetchGroups();
    }, []);

    // Fetch functions
    const fetchBoards = async () => {
        try {
            setPageLoading(true);
            const data = await BoardService.getBoards();
            setBoards(data);
        } catch (error) {
            message.error('Failed to fetch boards: ' + (error.response?.data?.message || 'Unknown error'));
        } finally {
            setPageLoading(false);
        }
    };

    const fetchProperties = async () => {
        try {
            const response = await BoardService.getProperties();
            setProperties(response.data);
        } catch (error) {
            message.error('Failed to fetch properties');
        }
    };

    const fetchManualClients = async () => {
        try {
            const response = await BoardService.getManualClients();
            setManualClients(response.data);
        } catch (error) {
            message.error('Failed to fetch manual clients');
        }
    };

    const fetchGroups = async () => {
        try {
            const response = await BoardService.getGroups();
            setGroups(response.data);
        } catch (error) {
            message.error('Failed to fetch groups');
        }
    };

    // Board operations
    const createBoard = async (values) => {
        try {
            setModalState(prev => ({ ...prev, loading: true }));

            // Prepare assignments
            const boardData = {
                ...values,
                assigned_properties: values.assigned_properties || [],
                manual_client_ids: values.manual_client_ids || [],
                group_ids: values.group_ids || []
            };

            const newBoard = await BoardService.createBoard(boardData);
            message.success('Board created successfully');
            window.location.href = `/tm/ui-boards/${newBoard.id}`;
            setBoards(prev => [...prev, newBoard]);
            setModalState({ visible: false, type: null, loading: false });
            return { success: true };
        } catch (error) {
            message.error('Failed to create board: ' + (error.response?.data?.message || 'Unknown error'));
            return { success: false, error };
        } finally {
            setModalState(prev => ({ ...prev, loading: false }));
        }
    };

    const updateBoard = async (boardId, values) => {
        try {
            setModalState(prev => ({ ...prev, loading: true }));

            // Prepare assignments
            const boardData = {
                ...values,
                assigned_properties: values.assigned_properties || [],
                manual_client_ids: values.manual_client_ids || [],
                group_ids: values.group_ids || []
            };

            const updatedBoard = await BoardService.update(boardId, boardData);
            setBoards(prev => prev.map(board =>
                board.id === boardId ? updatedBoard : board
            ));
            message.success('Board updated successfully');
            setModalState({ visible: false, type: null, loading: false });
            return { success: true };
        } catch (error) {
            message.error('Failed to update board: ' + (error.response?.data?.message || 'Unknown error'));
            return { success: false, error };
        } finally {
            setModalState(prev => ({ ...prev, loading: false }));
        }
    };

    const deleteBoard = async (boardId) => {
        try {
            setDeleteLoading(boardId);
            await BoardService.delete(boardId);
            setBoards(prev => prev.filter(b => b.id !== boardId));
            message.success('Board deleted successfully');
            return { success: true };
        } catch (error) {
            message.error('Failed to delete board: ' + (error.response?.data?.message || 'Unknown error'));
            return { success: false, error };
        } finally {
            setDeleteLoading(null);
        }
    };

    // Assignment operations
    const updateBoardAssignments = async (boardId, assignments) => {
        try {
            setModalState(prev => ({ ...prev, loading: true }));
            const updatedBoard = await BoardService.updateAssignments(boardId, assignments);
            setBoards(prev => prev.map(board =>
                board.id === boardId ? updatedBoard : board
            ));
            message.success('Board assignments updated successfully');
            return { success: true };
        } catch (error) {
            message.error('Failed to update board assignments');
            return { success: false, error };
        } finally {
            setModalState(prev => ({ ...prev, loading: false }));
        }
    };

    // Modal handlers
    const handleModalOpen = (type, board = null) => {
        setCurrentBoard(board);
        setModalState({ visible: true, type, loading: false });
    };

    const handleModalClose = () => {
        setModalState({ visible: false, type: null, loading: false });
        setCurrentBoard(null);
    };

    const getAssignmentOptions = () => {
        return {
            properties: properties.map(prop => ({
                label: prop.name,
                value: prop.id
            })),
            manualClients: manualClients.map(client => ({
                label: client.client_name,
                value: client.id
            })),
            groups: groups.map(group => ({
                label: group.group_name,
                value: group.id
            }))
        };
    };

    return {
        // State
        boards,
        currentBoard,
        modalState,
        pageLoading,
        deleteLoading,
        properties,
        manualClients,
        groups,

        // Actions
        createBoard,
        updateBoard,
        deleteBoard,
        updateBoardAssignments,
        handleModalOpen,
        handleModalClose,
        getAssignmentOptions,

        // Fetch functions
        fetchBoards,
        fetchProperties,
        fetchManualClients,
        fetchGroups,

        // Setters
        setBoards,
        setCurrentBoard,
        setModalState,
        setProperties,
        setManualClients,
        setGroups
    };
};
